import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3302';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export type U3302CategoryOrigin = 'SYSTEM' | 'MANUAL';
export type U3302CategoryDateMode = 'SINGLE' | 'INTERVAL';

export interface U3302InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  categoryOrigins: U3302CategoryOrigin[];
}

export interface U3302CategorySearchRow {
  id: string;
  code: string;
  label: string;
  colorCode: string;
  effectiveColorCode: string;
  origin: U3302CategoryOrigin;
  linkedLogicalUnit?: string | null;
  editableInCalendar: boolean;
  active: boolean;
  dateMode: U3302CategoryDateMode;
  version: number;
}

export interface U3302CategoryGet {
  id: string;
  code: string;
  label: string;
  colorCode: string;
  effectiveColorCode: string;
  origin: U3302CategoryOrigin;
  linkedLogicalUnit?: string | null;
  sourceEntityType?: string | null;
  editableInCalendar: boolean;
  active: boolean;
  dateMode: U3302CategoryDateMode;
  generateWeekendEvents: boolean;
  version: number;
  activeEventCount?: number;
}

export interface U3302CategoryCreateBody {
  code: string;
  label: string;
  colorCode: string;
  editableInCalendar: boolean;
  active: boolean;
  dateMode: U3302CategoryDateMode;
  generateWeekendEvents: boolean;
}

export interface U3302CategoryUpdateBody {
  id: string;
  version: number;
  label?: string;
  colorCode?: string;
  editableInCalendar?: boolean;
  active?: boolean;
  dateMode?: U3302CategoryDateMode;
  generateWeekendEvents?: boolean;
  orgColorOverride?: { colorCode: string } | null;
}

export interface U3302CategoryDeleteBody {
  id: string;
  version: number;
}

export interface U3302CategoryFormRow {
  id: string;
  code: string;
  label: string;
  colorCode: string;
  effectiveColorCode: string;
  origin: U3302CategoryOrigin;
  linkedLogicalUnit: string | null;
  editableInCalendar: boolean;
  active: boolean;
  dateMode: U3302CategoryDateMode;
  generateWeekendEvents: boolean;
  version: number;
  activeEventCount: number;
  /** Override couleur org (SYSTEM) — null = reset, undefined = inchangé côté UI tant que non touché. */
  orgColorOverrideCode: string | null;
  orgColorOverrideTouched: boolean;
}

export function createEmptyU3302CategoryFormRow(): U3302CategoryFormRow {
  return {
    id: '',
    code: '',
    label: '',
    colorCode: '#2563EB',
    effectiveColorCode: '#2563EB',
    origin: 'MANUAL',
    linkedLogicalUnit: null,
    editableInCalendar: true,
    active: true,
    dateMode: 'SINGLE',
    generateWeekendEvents: false,
    version: 0,
    activeEventCount: 0,
    orgColorOverrideCode: null,
    orgColorOverrideTouched: false,
  };
}

export function normalizeU3302CategoryFromApi(row: U3302CategoryGet): U3302CategoryFormRow {
  const hasOverride = row.effectiveColorCode !== row.colorCode;
  return {
    id: row.id,
    code: row.code,
    label: row.label,
    colorCode: row.colorCode,
    effectiveColorCode: row.effectiveColorCode,
    origin: row.origin,
    linkedLogicalUnit: row.linkedLogicalUnit ?? null,
    editableInCalendar: row.editableInCalendar,
    active: row.active,
    dateMode: row.dateMode ?? 'SINGLE',
    generateWeekendEvents: row.generateWeekendEvents ?? false,
    version: row.version,
    activeEventCount: row.activeEventCount ?? 0,
    orgColorOverrideCode: hasOverride ? row.effectiveColorCode : null,
    orgColorOverrideTouched: false,
  };
}

export function buildCategoryCreateBody(data: U3302CategoryFormRow): U3302CategoryCreateBody {
  return {
    code: data.code.trim().toUpperCase(),
    label: data.label.trim(),
    colorCode: data.colorCode.trim(),
    editableInCalendar: data.editableInCalendar,
    active: data.active,
    dateMode: data.dateMode,
    generateWeekendEvents: data.generateWeekendEvents,
  };
}

export function buildCategoryUpdateBody(data: U3302CategoryFormRow): U3302CategoryUpdateBody {
  if (data.origin === 'SYSTEM') {
    const body: U3302CategoryUpdateBody = {
      id: data.id,
      version: data.version,
    };
    if (data.orgColorOverrideTouched) {
      body.orgColorOverride =
        data.orgColorOverrideCode && data.orgColorOverrideCode.trim() !== ''
          ? { colorCode: data.orgColorOverrideCode.trim() }
          : null;
    }
    return body;
  }

  return {
    id: data.id,
    version: data.version,
    label: data.label.trim(),
    colorCode: data.colorCode.trim(),
    editableInCalendar: data.editableInCalendar,
    active: data.active,
    dateMode: data.dateMode,
    generateWeekendEvents: data.generateWeekendEvents,
  };
}

export function buildCategoryDeleteBody(data: Pick<U3302CategoryFormRow, 'id' | 'version'>): U3302CategoryDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearchCategories = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetCategory = (axios: AxiosInstance, id: string) => {
  return axios.get(LogicalUnitEndpoints.GET(id));
};

export const doCreateCategory = (axios: AxiosInstance, body: U3302CategoryCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateCategory = (axios: AxiosInstance, id: string, body: U3302CategoryUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteCategory = (axios: AxiosInstance, body: U3302CategoryDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(body.id), body);
};

export const doGetCategoryAudits = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(String(id)), { params });
};

export const luHandleAppiError = (_t: TranslateFn, _ns: string, _errorResult: U2000ApiErrorResult): U2000ApiErrorResult => {
  return _errorResult;
};
